import { InvestorDetailsSummaryTabHouseholdSummaryQuery } from '@/__generated__/graphql';
import { GroupedIcon } from '@/assets/icons/info-icons/groupedIcon';
import { MinusIcon } from '@/assets/icons/info-icons/minusIcon';
import { PlusIcon } from '@/assets/icons/info-icons/plusIcon';
import FormattingTooltip from '@/components/basicComponents/tooltip/dollarsTooltip';
import RedirectTitleCell from '@/components/table/redirectTitleCell';
import { TableRowCell, TableRowWrapper, TableWrapper, TitleCell } from '@/components/table/styles';
import { TableColumns } from '@/components/table/types';
import { themeVar } from '@/styles/theme';
import { AnimatePresence, motion } from 'framer-motion';
import styled from 'styled-components';

interface AllEntitiesSummaryTableRowProps {
  tableColumns: TableColumns[];
  tableRow: InvestorDetailsSummaryTabHouseholdSummaryQuery['investorHouseholdSummary']['data'][number];
  showSpecificColumns: boolean;
  isExpanded: boolean;
  onToggleExpanded: () => void;
  handleEntitySelect: ({ entityId, isGroup }: { entityId: string; isGroup: boolean }) => void;
}

const AllEntitiesSummaryTableRow = ({
  tableColumns,
  tableRow,
  showSpecificColumns,
  isExpanded,
  onToggleExpanded,
  handleEntitySelect
}: AllEntitiesSummaryTableRowProps) => {
  const WIDTH = 100 / tableColumns.length;
  const isGroup = tableRow?.groupedLegalEntitiesSummary?.length ? true : false;
  return (
    <>
      <TableRowWrapper>
        <EntityNameTableRowCell width={WIDTH}>
          {isGroup && <PlusIconWrapper onClick={onToggleExpanded}>{isExpanded ? <MinusIcon /> : <PlusIcon />}</PlusIconWrapper>}
          <RedirectTitleCell onClick={() => handleEntitySelect({ entityId: tableRow.primaryLegalEntityId, isGroup })}>
            {tableRow.name}
          </RedirectTitleCell>
        </EntityNameTableRowCell>
        {showSpecificColumns && (
          <TableRowCell width={WIDTH}>
            <TextCell bold={Boolean(isGroup)}>
              {tableRow.ignoreAllocationTargets ? <>--</> : <FormattingTooltip zIndex={1000}>{+tableRow.totalEntityValue}</FormattingTooltip>}
            </TextCell>
          </TableRowCell>
        )}
        {showSpecificColumns && (
          <TableRowCell width={WIDTH}>
            <TextCell bold={Boolean(tableRow?.groupedLegalEntitiesSummary?.length)}>
              {tableRow.ignoreAllocationTargets ? <>--</> : <>{tableRow.privateCapitalPercentage?.toFixed(2)}%</>}
            </TextCell>
          </TableRowCell>
        )}
        <TableRowCell width={WIDTH}>
          <TextCell bold={Boolean(isGroup)}>
            <FormattingTooltip zIndex={1000}>{tableRow.currentNAV}</FormattingTooltip>
          </TextCell>
        </TableRowCell>
        <TableRowCell width={WIDTH}>
          <TextCell bold={Boolean(isGroup)}>
            <FormattingTooltip zIndex={1000}>{tableRow.unfundedCommitment}</FormattingTooltip>
          </TextCell>
        </TableRowCell>
        {showSpecificColumns && (
          <TableRowCell width={WIDTH}>
            <TextCell bold={Boolean(isGroup)}>
              {tableRow.ignoreAllocationTargets ? (
                '--'
              ) : tableRow.targetOverCommitmentRatio === null ? (
                <>Pacing Model</>
              ) : (
                <>OC Ratio {tableRow.targetOverCommitmentRatio}</>
              )}
            </TextCell>
          </TableRowCell>
        )}
        {showSpecificColumns && (
          <TableRowCell width={WIDTH}>
            <TextCell bold={Boolean(isGroup)}>
              {tableRow.ignoreAllocationTargets || tableRow.capacity === null ? (
                '--'
              ) : (
                <FormattingTooltip zIndex={1000}>{+tableRow.capacity}</FormattingTooltip>
              )}
            </TextCell>
          </TableRowCell>
        )}
      </TableRowWrapper>
      <AnimatePresence>
        {isExpanded && isGroup && (
          <motion.div
            key={`${tableRow.primaryLegalEntityId}-grouped-entities`}
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
          >
            <TableWrapper padding="0">
              {tableRow?.groupedLegalEntitiesSummary?.map((groupedEntity) => (
                <TableRowWrapper key={groupedEntity.id}>
                  <GroupedEntityNameTableRowCell width={WIDTH}>
                    <GroupIconWrapper>
                      <GroupedIcon width={26} height={26} />
                    </GroupIconWrapper>
                    <RedirectTitleCell onClick={() => handleEntitySelect({ entityId: groupedEntity.id, isGroup: false })}>
                      {groupedEntity.name}
                    </RedirectTitleCell>
                  </GroupedEntityNameTableRowCell>
                  {showSpecificColumns && (
                    <TableRowCell width={WIDTH}>
                      <TextCell>
                        {tableRow.ignoreAllocationTargets ? (
                          <>--</>
                        ) : (
                          <FormattingTooltip zIndex={1000}>{+groupedEntity.totalEntityValue}</FormattingTooltip>
                        )}
                      </TextCell>
                    </TableRowCell>
                  )}
                  {showSpecificColumns && (
                    <TableRowCell width={WIDTH}>
                      <TextCell>--</TextCell>
                    </TableRowCell>
                  )}
                  <TableRowCell width={WIDTH}>
                    <TextCell>
                      {tableRow.ignoreAllocationTargets ? <>--</> : <FormattingTooltip zIndex={1000}>{+groupedEntity.currentNAV}</FormattingTooltip>}
                    </TextCell>
                  </TableRowCell>
                  <TableRowCell width={WIDTH}>
                    <TextCell>
                      <FormattingTooltip>{groupedEntity.unfundedCommitment}</FormattingTooltip>
                    </TextCell>
                  </TableRowCell>
                  {showSpecificColumns && (
                    <TableRowCell width={WIDTH}>
                      <TextCell>--</TextCell>
                    </TableRowCell>
                  )}
                  {showSpecificColumns && (
                    <TableRowCell width={WIDTH}>
                      <TextCell>--</TextCell>
                    </TableRowCell>
                  )}
                </TableRowWrapper>
              ))}
            </TableWrapper>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default AllEntitiesSummaryTableRow;

const EntityNameTableRowCell = styled(TableRowCell)`
  gap: 6px;
`;

const GroupedEntityNameTableRowCell = styled(TableRowCell)`
  align-items: center;
`;

const TextCell = styled(TitleCell)<{ bold?: boolean }>`
  font-weight: ${({ bold }) => (bold ? 600 : 400)};
`;

const PlusIconWrapper = styled.div`
  cursor: pointer;
  border-radius: 12px;
  width: 24px;
  height: 24px;
  display: flex;
  justify-content: center;
  align-items: center;

  svg {
    path {
      fill: ${themeVar('font.base')};
    }
  }

  &:hover {
    background-color: ${({ theme }) => theme.action.hover};
    transition: background-color 100ms ease;

    svg {
      path {
        fill: ${themeVar('context.light')};
      }
    }
  }
`;

const GroupIconWrapper = styled.div`
  padding: 5px;
  position: relative;
  top: -9px;
  margin-right: 14px;
  margin-left: 5px;
`;
